import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import type { SubmissionRequirement } from '@/lib/schemas'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

interface SubmissionRequirementsState {
  // State
  requirements: SubmissionRequirement[]
  isLoading: boolean
  error: string | null
  currentBidId: string | null

  // Filter state
  showSuperseded: boolean

  // Actions - Fetching
  fetchRequirements: (bidId: string) => Promise<void>

  // Actions - CRUD
  updateRequirement: (id: string, data: Partial<SubmissionRequirement>) => Promise<SubmissionRequirement>

  // Actions - Utility
  clearError: () => void
  reset: () => void
  setShowSuperseded: (show: boolean) => void

  // Computed
  getActiveRequirements: () => SubmissionRequirement[]
  getRequirementsByType: (type: SubmissionRequirement['requirementType']) => SubmissionRequirement[]
  getSubmissionDeadline: () => SubmissionRequirement | undefined
  getLodgementMethod: () => SubmissionRequirement | undefined
  getRequiredForms: () => SubmissionRequirement[]
  getUpcomingDeadlines: () => SubmissionRequirement[]
  getRequirementById: (id: string) => SubmissionRequirement | undefined
}

export const useSubmissionRequirementsStore = create<SubmissionRequirementsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      requirements: [],
      isLoading: false,
      error: null,
      currentBidId: null,
      showSuperseded: false,

      // Fetch requirements for a bid
      fetchRequirements: async (bidId: string) => {
        try {
          set({ isLoading: true, error: null, currentBidId: bidId })
          const requirements = await api.get<SubmissionRequirement[]>(`/bids/${bidId}/submission-requirements`)
          set({ requirements, isLoading: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch submission requirements',
            isLoading: false,
          })
        }
      },

      // Update a requirement
      updateRequirement: async (id: string, data: Partial<SubmissionRequirement>) => {
        try {
          set({ error: null })
          const requirement = await api.patch<SubmissionRequirement>(`/submission-requirements/${id}`, data)
          set((state) => ({
            requirements: state.requirements.map((r) => (r.id === id ? requirement : r)),
          }))
          return requirement
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update submission requirement',
          })
          throw error
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store state
      reset: () => {
        set({
          requirements: [],
          isLoading: false,
          error: null,
          currentBidId: null,
          showSuperseded: false,
        })
      },

      // Toggle showing superseded items
      setShowSuperseded: (show: boolean) => {
        set({ showSuperseded: show })
      },

      // Get active (non-superseded) requirements
      getActiveRequirements: () => {
        const { requirements, showSuperseded } = get()
        if (showSuperseded) return requirements
        return requirements.filter((r) => r.artifactStatus === 'active')
      },

      // Get requirements by type
      getRequirementsByType: (type: SubmissionRequirement['requirementType']) => {
        const activeRequirements = get().getActiveRequirements()
        return activeRequirements.filter((r) => r.requirementType === type)
      },

      // Get submission deadline (first deadline type requirement)
      getSubmissionDeadline: () => {
        const deadlines = get().getRequirementsByType('deadline')
        // Sort by deadline date and return earliest
        return deadlines
          .filter((d) => d.deadlineDate)
          .sort((a, b) => {
            if (!a.deadlineDate || !b.deadlineDate) return 0
            return new Date(a.deadlineDate).getTime() - new Date(b.deadlineDate).getTime()
          })[0]
      },

      // Get lodgement method
      getLodgementMethod: () => {
        const lodgementMethods = get().getRequirementsByType('lodgement_method')
        return lodgementMethods[0]
      },

      // Get required forms
      getRequiredForms: () => {
        return get().getRequirementsByType('form')
      },

      // Get upcoming deadlines (sorted by date)
      getUpcomingDeadlines: () => {
        const activeRequirements = get().getActiveRequirements()
        const now = new Date()
        return activeRequirements
          .filter((r) => r.deadlineDate && new Date(r.deadlineDate) > now)
          .sort((a, b) => {
            if (!a.deadlineDate || !b.deadlineDate) return 0
            return new Date(a.deadlineDate).getTime() - new Date(b.deadlineDate).getTime()
          })
      },

      // Get requirement by ID
      getRequirementById: (id: string) => {
        return get().requirements.find((r) => r.id === id)
      },
    }),
    { name: 'submission-requirements-store' }
  )
)
